import React, { useState, useEffect } from 'react';
import { User, CheckSquare, AlertTriangle, FileText } from 'lucide-react';
import { makeRequest2 } from '../../../utils/makeRequest';
import { addNewUserURL, GET_DEPARTMENT_URL } from '../../../utils/urls';
import { toastify } from '../../../utils/toast';
import { getItem } from '../../../utils/localStorage';

const AddUserTab = () => {
    const [permissions, setPermissions] = useState({});
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [email, setEmail] = useState('');
    const [department, setDepartment] = useState('');
    const [departments, setDepartments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [role, setRole] = useState('Staff');
    const [canReceiveReports, setCanReceiveReports] = useState({
        water: false,
        levy: false,
        power: false,
        lease: false,
    });

    const modules = [
        { name: 'visitorAccess', displayName: 'Visitor', permissions: ['create', 'read', 'update', 'delete'] },
        { name: 'levy', displayName: 'Levy', permissions: ['create', 'read', 'update', 'delete'] },
        { name: 'maintenance', displayName: 'Maintenance', permissions: ['create', 'read', 'update', 'delete', 'approve'] },
        { name: 'lease', displayName: 'Lease', permissions: ['create', 'read', 'update', 'delete'] },
        { name: 'vas', displayName: 'VAS', permissions: ['create', 'read', 'update', 'delete'] },
        { name: 'tickets', displayName: 'Tickets', permissions: ['create', 'read', 'update', 'delete', 'review'] },
        { name: 'utility', displayName: 'Utility', permissions: ['create', 'read', 'update', 'delete', 'approve'] },
        { name: 'booking', displayName: 'Booking', permissions: ['create', 'read', 'update', 'delete'] },
        { name: 'handover', displayName: 'Handover', permissions: ['create', 'read', 'update', 'delete'] },
        { name: 'accounts', displayName: 'Expense', permissions: ['create', 'read', 'update', 'delete'] },
        { name: 'campaigns', displayName: 'Campaign', permissions: ['create', 'read', 'update', 'delete'] },
        { name: 'procurement', displayName: 'Procurement', permissions: ['create', 'read', 'update', 'delete', 'approve'] },
        { name: 'propertyManagement', displayName: 'PropertyManagement', permissions: ['create', 'read', 'update', 'delete', 'approve'] }
    ];

    const reportTypes = [
        { key: 'water', label: 'Water Reports' },
        { key: 'levy', label: 'Levy Reports' },
        { key: 'power', label: 'Power Reports' },
        { key: 'lease', label: 'Lease Reports' },
    ];

    useEffect(() => {
        fetchDepartments();
    }, []);

    const fetchDepartments = async () => {
        try {
            const facilityId = await getItem('selectedFacilityId');
            const response = await makeRequest2(`${GET_DEPARTMENT_URL}/${facilityId}`, 'GET');

            if (response.success) {
                const departmentData = response.data || [];
                let departmentsArray = [];
                if (Array.isArray(departmentData)) {
                    departmentsArray = departmentData;
                } else if (departmentData.data && Array.isArray(departmentData.data)) {
                    departmentsArray = departmentData.data;
                } else if (departmentData && typeof departmentData === 'object') {
                    departmentsArray = [departmentData];
                }
                setDepartments(departmentsArray);
            } else {
                toastify(response.error || 'Failed to fetch departments', 'error');
                setDepartments([]);
            }
        } catch (error) {
            toastify('Error fetching departments: ' + error.message, 'error');
            setDepartments([]);
        }
    };

    const handleAddUser = async () => {
        const facilityId = await getItem('selectedFacilityId');

        if (!firstName.trim() || !lastName.trim() || !phoneNumber.trim() || !email.trim()) {
            toastify('Please fill in all required fields', 'error');
            return;
        }

        setLoading(true);
        try {
            const userData = {
                firstName,
                lastName,
                phoneNumber,
                email,
                department: department || null,
                permissions,
                canReceiveReports,
                role,
            };

            const response = await makeRequest2(`${addNewUserURL}/${facilityId}`, 'POST', userData);

            if (response.success) {
                toastify('User added successfully', 'success');
                setFirstName('');
                setLastName('');
                setPhoneNumber('');
                setEmail('');
                setDepartment('');
                setPermissions({});
                setRole('Staff');
                setCanReceiveReports({ water: false, levy: false, power: false, lease: false });
            } else {
                throw new Error(response.error || 'Failed to add user');
            }
        } catch (err) {
            toastify(err.message || 'Error adding user', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleClear = () => {
        setFirstName('');
        setLastName('');
        setPhoneNumber('');
        setEmail('');
        setDepartment('');
        setPermissions({});
        setRole('Staff');
        setCanReceiveReports({ water: false, levy: false, power: false, lease: false });
    };

    const handlePermissionChange = (module, permission, checked) => {
        setPermissions(prev => ({
            ...prev,
            [module]: { ...(prev[module] || {}), [permission]: checked }
        }));
    };

    const selectAllPermissions = (module, checked) => {
        const modulePermissions = {};
        modules.find(m => m.name === module).permissions.forEach(p => {
            modulePermissions[p] = checked;
        });
        setPermissions(prev => ({ ...prev, [module]: modulePermissions }));
    };

    const handleReportToggle = (key) => {
        setCanReceiveReports(prev => ({ ...prev, [key]: !prev[key] }));
    };

    return (
        <div className="container-fluid p-4">
            <div className="card">
                <div className="card-header bg-white py-3">
                    <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0 font-weight-bold text-dark">
                            <User size={20} className="me-2" /> Add New User
                        </h5>
                    </div>
                </div>

                <div className="card-body">
                    <div>
                        <div className="row mb-4">
                            <div className="col-md-6 mb-3">
                                <label className="form-label">First Name *</label>
                                <input
                                    type="text"
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    className="form-control"
                                    required
                                />
                            </div>
                            <div className="col-md-6 mb-3">
                                <label className="form-label">Last Name *</label>
                                <input
                                    type="text"
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                    className="form-control"
                                    required
                                />
                            </div>
                            <div className="col-md-6 mb-3">
                                <label className="form-label">Phone Number *</label>
                                <input
                                    type="text"
                                    value={phoneNumber}
                                    onChange={(e) => setPhoneNumber(e.target.value)}
                                    className="form-control"
                                    required
                                />
                            </div>
                            <div className="col-md-6 mb-3">
                                <label className="form-label">Email *</label>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="form-control"
                                    required
                                />
                            </div>
                            <div className="col-md-6 mb-3">
                                <label className="form-label">Department</label>
                                <select
                                    value={department}
                                    onChange={(e) => setDepartment(e.target.value)}
                                    className="form-select"
                                >
                                    <option value="">Select Department (Optional)</option>
                                    {departments.map((dept) => (
                                        <option key={dept._id} value={dept._id}>{dept.name}</option>
                                    ))}
                                </select>
                                {departments.length === 0 && (
                                    <small className="text-muted">No departments available. Please create departments first.</small>
                                )}
                            </div>
                            <div className="col-md-6 mb-3">
                                <label className="form-label">Role *</label>
                                <select
                                    value={role}
                                    onChange={(e) => setRole(e.target.value)}
                                    className="form-select"
                                >
                                    <option value="Staff">Staff</option>
                                    <option value="admin">Admin</option>
                                    <option value="editor">Editor</option>
                                    <option value="user">User</option>
                                    <option value="ninja">Ninja (Full Access)</option>
                                </select>
                            </div>
                        </div>

                        {/* Permissions Table */}
                        <div className="mt-4 mb-4">
                            <div className="d-flex align-items-center mb-3">
                                <CheckSquare size={20} className="text-primary me-2" />
                                <h6 className="mb-0 fw-bold">Roles & Permissions</h6>
                                <div className="ms-3 badge bg-light text-secondary d-flex align-items-center">
                                    <AlertTriangle size={14} className="text-warning me-1" />
                                    Select permissions for each module
                                </div>
                            </div>

                            <div className="table-responsive">
                                <table className="table table-bordered table-hover">
                                    <thead className="table-light">
                                        <tr>
                                            <th className="text-start" style={{ width: '16%' }}>Module</th>
                                            <th className="text-center">Create</th>
                                            <th className="text-center">Read</th>
                                            <th className="text-center">Update</th>
                                            <th className="text-center">Delete</th>
                                            <th className="text-center">Special</th>
                                            <th className="text-center" style={{ width: '16%' }}>All/None</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {modules.map((module) => (
                                            <tr key={module.name}>
                                                <td className="align-middle fw-medium">{module.displayName}</td>
                                                {['create', 'read', 'update', 'delete'].map(permission => (
                                                    <td key={permission} className="text-center align-middle">
                                                        <div className="form-check d-flex justify-content-center">
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                checked={permissions[module.name]?.[permission] || false}
                                                                onChange={(e) => handlePermissionChange(module.name, permission, e.target.checked)}
                                                            />
                                                        </div>
                                                    </td>
                                                ))}
                                                <td className="text-center align-middle">
                                                    {module.name === 'maintenance' || module.name === 'utility' || module.name === 'procurement' ? (
                                                        <div className="form-check d-flex justify-content-center align-items-center">
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                checked={permissions[module.name]?.approve || false}
                                                                onChange={(e) => handlePermissionChange(module.name, 'approve', e.target.checked)}
                                                            />
                                                            <small className="ms-1 text-muted">Approve</small>
                                                        </div>
                                                    ) : module.name === 'tickets' ? (
                                                        <div className="form-check d-flex justify-content-center align-items-center">
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                checked={permissions[module.name]?.review || false}
                                                                onChange={(e) => handlePermissionChange(module.name, 'review', e.target.checked)}
                                                            />
                                                            <small className="ms-1 text-muted">Review</small>
                                                        </div>
                                                    ) : (
                                                        <span className="text-muted">-</span>
                                                    )}
                                                </td>
                                                <td className="text-center align-middle">
                                                    <div className="d-flex justify-content-center gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => selectAllPermissions(module.name, true)}
                                                            className="btn btn-sm btn-outline-primary"
                                                        >All</button>
                                                        <button
                                                            type="button"
                                                            onClick={() => selectAllPermissions(module.name, false)}
                                                            className="btn btn-sm btn-outline-secondary"
                                                        >None</button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Report Subscriptions */}
                        <div className="mt-2 mb-4">
                            <div className="d-flex align-items-center mb-3">
                                <FileText size={20} className="text-primary me-2" />
                                <h6 className="mb-0 fw-bold">Report Subscriptions</h6>
                                <div className="ms-3 badge bg-light text-secondary">
                                    Select which reports this user will receive
                                </div>
                            </div>

                            <div className="row">
                                {reportTypes.map(({ key, label }) => (
                                    <div key={key} className="col-md-3 col-6 mb-3">
                                        <div
                                            className={`card border-2 h-100 cursor-pointer ${canReceiveReports[key] ? 'border-primary bg-primary bg-opacity-10' : 'border-light'}`}
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => handleReportToggle(key)}
                                        >
                                            <div className="card-body d-flex align-items-center gap-3 py-3">
                                                <div className="form-check mb-0">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        checked={canReceiveReports[key]}
                                                        onChange={() => handleReportToggle(key)}
                                                        onClick={(e) => e.stopPropagation()}
                                                        style={{ width: '1.1rem', height: '1.1rem' }}
                                                    />
                                                </div>
                                                <span className={`fw-medium ${canReceiveReports[key] ? 'text-primary' : 'text-secondary'}`}>
                                                    {label}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="d-flex justify-content-end">
                        <div className="text-end">
                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={handleClear}
                                disabled={loading}
                            >Clear</button>
                            &nbsp;
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleAddUser}
                                disabled={loading || !firstName.trim() || !lastName.trim() || !phoneNumber.trim() || !email.trim()}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Adding...
                                    </>
                                ) : 'Submit'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddUserTab;